// ─────────────────────────────────────────────────────────────────────────────
// PriceObservationEditDialogComponent — the ONE dialog
// ─────────────────────────────────────────────────────────────────────────────
// Client, product, price, dates and scope, editable even on an attached row.
// The correction writes in place: the verbatim quote stays as evidence, with
// the "price corrected on ..." mention, and the response carries the same id
// the store holds.
//
// Moving the customer or the product re-teaches the matcher server-side, so the
// next import does not repeat the mistake. Memorisation is always on.
//
// A conflict row shows the banner naming the rival price, and "keep this price"
// settles it server-side.
// ─────────────────────────────────────────────────────────────────────────────

import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';

import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { SharedDialogComponent } from 'src/app/ui/shared-dialog/shared-dialog.component';
import { ProductsService } from 'src/app/features/produits/services/produits.services';
import { CustomersService } from 'src/app/features/customers/services/customers.services';
import { SelectOption as FormSelectOption } from 'src/app/ui/form-field/form-field.component';

import {
  RememberReferenceDialogComponent,
  RememberReferenceDialogData,
} from '../remember-reference-dialog/remember-reference-dialog.component';
import {
  AudienceScopePickerComponent,
  IAudienceScope,
  isAudienceIncomplete,
  PriceAudience,
} from '../audience-scope-picker/audience-scope-picker.component';

import {
  IPriceMatchCandidate,
  IPriceObservation,
  IUpdatePriceObservationPayload,
  PriceObservationScope,
  candidateLabel,
  productLabel,
  sourceLabel,
} from '../../models/price-observation.models';

export interface PriceObservationEditDialogData {
  observation: IPriceObservation;
}

export type PriceObservationEditDialogResult =
  | { action: 'save'; payload: IUpdatePriceObservationPayload }
  | { action: 'delete' }
  | { action: 'keep-price' };

interface SelectOption {
  value: number;
  label: string;
}

const MIN_SEARCH_LENGTH = 2;

/** Same set, whatever the order the chips were added in. */
function sameIds(left: number[], right: number[]): boolean {
  if (left.length !== right.length) return false;
  const seen = new Set(right);
  return left.every((id) => seen.has(id));
}

@Component({
  selector: 'app-price-observation-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    SharedModule,
    UiModule,
    SharedDialogComponent,
    AudienceScopePickerComponent,
  ],
  templateUrl: './price-observation-edit-dialog.component.html',
  styleUrls: ['./price-observation-edit-dialog.component.scss'],
})
export class PriceObservationEditDialogComponent implements OnInit, OnDestroy {
  readonly observation: IPriceObservation;

  price: number | null;
  observedAt: string | null;
  validUntil: string | null;

  // The four fields the MAPPING screen lets an operator name in their file and
  // this dialog could not fix afterwards. All four have been writable through
  // the route all along (`_EDITABLE_FIELDS`): a misread unit or a currency the
  // reader assumed was visible on the row and correctable nowhere. The first
  // two are held by their control — a mirrored field would be a second answer
  // to keep in step.
  customerCodeRaw: string | null;
  productNameRaw: string | null;

  /** The currencies this instance knows, for the select above. */
  currencyOptions: FormSelectOption[] = [];
  /**
   * Fixed for the life of the dialog — `available_units` never changes here —
   * so it is built once: a getter handed `<option>` a new array every
   * change-detection cycle, and the select re-rendered each time.
   */
  unitOptions: FormSelectOption[] = [];
  currencyControl = new FormControl<number | null>(null);

  /**
   * The unit is chosen, never typed.
   *
   * `unit` is matched against the product's handling units by name and exactly,
   * case and surrounding spaces aside. A free-text box lets an operator
   * "correct" a unit into one the catalogue does not carry, and the row keeps
   * an unresolved unit with nothing on screen saying why.
   */
  unitControl = new FormControl<string | null>(null);

  /**
   * The audience, asked with the knowledge base's own picker.
   *
   * All three values: `SCOPE_GROUP` and the `group_ids` m2m are on the model
   * and editable through the route, so hiding the option leaves a row imported
   * for a group visible and unmovable. Customers stay single, groups multiple.
   */
  readonly scopeAudiences: PriceAudience[] = ['global', 'customer', 'group'];
  audienceScope: IAudienceScope;

  productControl = new FormControl('');
  productOptions$!: Observable<SelectOption[]>;
  /**
   * The picked product, and the text it was chosen under; null = unchanged.
   *
   * One field, because the two are one fact: an id surviving the field it came
   * from means clearing the product leaves it set, the suggestions never come
   * back, and SAVE attaches a product the operator just erased.
   */
  private picked: { id: number; label: string } | null = null;

  /** The picked id; null = unchanged. */
  get pickedProductTemplateId(): number | null {
    return this.picked?.id ?? null;
  }

  private destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<PriceObservationEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: PriceObservationEditDialogData,
    private productsService: ProductsService,
    private customersService: CustomersService,
    private dialog: MatDialog,
  ) {
    this.observation = data.observation;
    this.price = this.observation.price ?? null;
    this.observedAt = this.observation.observed_at ?? null;
    this.validUntil = this.observation.valid_until ?? null;
    this.customerCodeRaw = this.observation.customer_code_raw ?? null;
    this.productNameRaw = this.observation.product_name_raw ?? null;
    // `unresolved` is a verdict, not an audience, so the picker opens with no
    // answer -- which is what the row carries, and what `isAudienceIncomplete`
    // treats as legal on an existing row.
    //
    // Opening on "One customer" as a nudge bricks the dialog: with nobody named
    // the audience reads incomplete, SAVE stays off however the price or the
    // product is fixed, and `audiencePayload` restates `scope: customer` on
    // every save.
    this.audienceScope = {
      audience:
        this.observation.scope === 'global'
        || this.observation.scope === 'group'
        || this.observation.scope === 'customer'
          ? this.observation.scope
          : null,
      // ONE customer here, and the picker is told so: this dialog is looking
      // at a single price row, whose `customer_id` is a Many2one. The upload
      // names several — same picker, `maxCustomers` apart.
      customers: this.observation.customer_id
        ? [{
            id: this.observation.customer_id,
            name: this.observation.customer_name ?? '',
          }]
        : [],
      groupIds: this.observation.group_ids ?? [],
    };
  }

  ngOnInit(): void {
    this.currencyControl.setValue(this.observation.currency_id ?? null, { emitEvent: false });
    this.unitControl.setValue(this.observation.unit ?? null, { emitEvent: false });
    this.unitOptions = this.buildUnitOptions();

    this.customersService
      .listAllCurrencies()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.currencyOptions = (response.data ?? [])
            .filter((currency) => currency.is_active)
            .map((currency) => ({ value: currency.id, label: currency.name }));
        },
        // A failed lookup leaves the select empty rather than the dialog
        // broken: every other field stays correctable.
        error: () => (this.currencyOptions = []),
      });

    this.productControl.setValue(this.currentProductLabel);
    // Typing over the picked product, or clearing it, un-picks it: what the
    // field says and what SAVE sends must not be two different answers.
    this.productControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (!this.picked) return;
        if (this.displayOptionFn(value ?? '').trim() !== this.picked.label.trim()) {
          this.picked = null;
        }
      });
    // The product travels as the TEMPLATE id — the only form the backend
    // accepts. A variant id can silently attach the wrong product when the
    // two integers happen to collide.
    this.productOptions$ = this.autocomplete(this.productControl, (term) =>
      this.productsService
        .fetchProductsBySearch({ number: 1, size: 20 }, { filters: [] }, term, undefined, false, true)
        .pipe(
          map((res) =>
            (res.data ?? [])
              .filter((product) => product.product_template_id != null)
              .map((product) => ({
                value: product.product_template_id!,
                label: product.sku ? `${product.sku} — ${product.name}` : product.name,
              })),
          ),
        ),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private autocomplete(
    control: FormControl,
    search: (term: string) => Observable<SelectOption[]>,
  ): Observable<SelectOption[]> {
    return control.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) => {
        const keyword = typeof term === 'string' ? term : '';
        if (!keyword || keyword.length < MIN_SEARCH_LENGTH) {
          return of([] as SelectOption[]);
        }
        return search(keyword);
      }),
      takeUntil(this.destroy$),
    );
  }

  displayOptionFn(option: SelectOption | string): string {
    return typeof option === 'string' ? option : (option?.label ?? '');
  }

  onAudienceScopeChange(scope: IAudienceScope): void {
    this.audienceScope = scope;
  }

  onProductSelected(option: SelectOption): void {
    this.picked = { id: option.value, label: option.label };
  }

  /**
   * Matcher suggestions for an unresolved product, shown while the row has no
   * product -- not "until one is clicked".
   *
   * A click is a proposal filled into the field and SAVE is the decision, so
   * the alternatives stay and the one in the field is marked: emptying the list
   * on a click means changing one's mind requires reopening the dialog.
   *
   * Applying one fills the same field a manual search would, travels as
   * `product_template_id` and re-teaches the matcher like any human choice.
   */
  get suggestions(): IPriceMatchCandidate[] {
    if (this.observation.product_id) return [];
    return this.observation.match_candidates ?? [];
  }

  /** The candidate currently sitting in the product field, if it is one. */
  isPicked(candidate: IPriceMatchCandidate): boolean {
    return (
      candidate.product_id != null
      && candidate.product_id === this.pickedProductTemplateId
    );
  }

  /**
   * A candidate as one line: code, then designation. No score.
   *
   * The number is the reranker's, not a fact about the product, and nothing an
   * operator does changes at 85 rather than 40 -- while the `0%` the matcher
   * returns for an unscored alternative paints a good second choice as noise.
   * The rank is the information, and the list is already in it.
   */
  suggestionLabel = candidateLabel;

  applySuggestion(candidate: IPriceMatchCandidate): void {
    // A sku the local catalogue does not hold cannot be applied in one
    // click — the row is disabled in the template for that case.
    if (candidate.product_id == null) return;
    // Id and label written TOGETHER, before the field: writing the field
    // emits `valueChanges` synchronously, and the watcher there drops the
    // pick whose label no longer matches the text.
    this.picked = { id: candidate.product_id, label: this.suggestionLabel(candidate) };
    // The same text the row showed: what the operator clicked is what the
    // field must then read.
    this.productControl.setValue(this.picked.label);
  }

  // ── Computed ──────────────────────────────────────────────────────────────

  get currentProductLabel(): string {
    return productLabel(this.observation);
  }

  /**
   * A named audience with nobody in it is the dash the list draws: neither "for
   * everyone" nor "for someone", and no lookup can serve it. Switching the
   * audience without picking anyone is the only way to produce it here, so SAVE
   * waits.
   *
   * The same wall stands in `revise()` server-side; this one spares the round
   * trip.
   */
  get audienceIncomplete(): boolean {
    return isAudienceIncomplete(this.audienceScope);
  }

  /**
   * The operator typed in the product field without choosing an option.
   *
   * The field is an autocomplete over the catalogue and only a PICKED option
   * carries an id, so free text was simply dropped at save: the dialog closed,
   * the toast said saved, and the product had not moved. It is a blocker now,
   * with a sentence saying what to do.
   */
  get productTextUnconfirmed(): boolean {
    if (this.pickedProductTemplateId != null) return false;
    const typed = (this.productControl.value ?? '').toString().trim();
    return typed.length > 0 && typed !== this.currentProductLabel.trim();
  }

  /**
   * An observation with no price is not an observation. Emptying the field
   * used to travel as "no change" — the value silently stayed the old one.
   */
  get priceMissing(): boolean {
    return this.price == null || (this.price as unknown as string) === '';
  }

  get blockedReasonKey(): string | null {
    if (this.priceMissing) return 'knowledge_base.price_observations.edit.price_required';
    if (this.productTextUnconfirmed) return 'knowledge_base.price_observations.edit.product_unconfirmed';
    if (this.audienceIncomplete) {
      return this.audienceScope.audience === 'group'
        ? 'knowledge_base.price_observations.edit.group_required'
        : 'knowledge_base.price_observations.edit.customer_required';
    }
    return null;
  }

  get canSave(): boolean {
    return this.hasChanges && this.blockedReasonKey === null;
  }

  // ── The unit, chosen from the product's own ─────────────────────────────

  /**
   * What the select offers: the product's units, plus the row's own written one
   * when the catalogue does not carry it.
   *
   * That last entry is the case this screen exists for -- a file wrote "CT"
   * where the product sells in PZ and CF -- and dropping it would erase the
   * evidence. It is offered, marked, and replaceable by one that resolves.
   */
  private buildUnitOptions(): FormSelectOption[] {
    const options: FormSelectOption[] = (this.observation.available_units ?? []).map((option) => ({
      value: option.name,
      label: option.name,
    }));
    const written = (this.observation.unit ?? '').trim();
    if (written && this.unitUnresolved) {
      options.unshift({ value: written, label: written });
    }
    return options;
  }

  /**
   * The row carries a unit the product does not sell in — so it resolved to
   * nothing, and the price is not comparable to anything.
   */
  get unitUnresolved(): boolean {
    const written = (this.observation.unit ?? '').trim();
    if (!written) return false;
    return !(this.observation.available_units ?? []).some(
      (option) => option.name.trim().toUpperCase() === written.toUpperCase(),
    );
  }

  /**
   * Nothing to choose from: the product is not resolved, so there is no
   * catalogue to read units off. The product question comes first.
   */
  get unitNeedsProduct(): boolean {
    return !this.observation.product_id;
  }

  get hasQuote(): boolean {
    return !!this.observation.source_quote;
  }

  get sourceLine(): string {
    return sourceLabel(this.observation, '');
  }

  get isConflict(): boolean {
    return this.observation.state === 'conflict';
  }

  /** The rival the banner names — first of `conflict_with`. */
  get rivalLabel(): string {
    const rival = this.observation.conflict_with?.[0];
    if (!rival) return '';
    const date = rival.observed_at ?? '';
    return `${rival.price} ${rival.currency ?? ''} · ${date}`.trim();
  }

  /**
   * WHERE the rival price was read from — the same `file · row` the row above
   * cites for this one.
   *
   * The banner used to name two amounts and a date and ask the operator to
   * pick one. What decides it is the document each came from, so it is named:
   * the same function the evidence block above uses, so the two lines cannot
   * spell one file two ways.
   */
  get rivalSource(): string {
    const rival = this.observation.conflict_with?.[0];
    if (!rival?.source_filename) return '';
    return rival.source_row ? `${rival.source_filename} · ${rival.source_row}` : rival.source_filename;
  }

  /** The rival's own line, verbatim — empty when it carries no quote. */
  get rivalQuote(): string {
    return this.observation.conflict_with?.[0]?.source_quote ?? '';
  }

  get isCorrected(): boolean {
    return this.observation.corrected_manually;
  }

  get hasChanges(): boolean {
    return Object.keys(this.payload).length > 0;
  }

  /**
   * Only what actually moved travels. "Enregistrer" without a change closes
   * the dialog and does NOTHING — visibly disabled, never a silent no-op.
   */
  private get payload(): IUpdatePriceObservationPayload {
    const payload: IUpdatePriceObservationPayload = {};
    if (this.price !== (this.observation.price ?? null) && this.price != null) {
      payload.price = this.price;
    }
    if ((this.observedAt || null) !== (this.observation.observed_at ?? null)) {
      payload.observed_at = this.observedAt || null;
    }
    if ((this.validUntil || null) !== (this.observation.valid_until ?? null)) {
      payload.valid_until = this.validUntil || null;
    }
    if (
      this.pickedProductTemplateId != null &&
      this.pickedProductTemplateId !== this.observation.product_id
    ) {
      payload.product_template_id = this.pickedProductTemplateId;
    }
    const currencyId = this.currencyControl.value ?? null;
    if (currencyId != null && currencyId !== (this.observation.currency_id ?? null)) {
      payload.currency_id = currencyId;
    }
    const unit = this.normalised(this.unitControl.value ?? null);
    if (unit !== (this.observation.unit ?? null)) {
      payload.unit = unit;
    }
    if (this.normalised(this.customerCodeRaw) !== (this.observation.customer_code_raw ?? null)) {
      payload.customer_code_raw = this.normalised(this.customerCodeRaw);
    }
    if (this.normalised(this.productNameRaw) !== (this.observation.product_name_raw ?? null)) {
      payload.product_name_raw = this.normalised(this.productNameRaw);
    }
    return { ...payload, ...this.audiencePayload };
  }

  /** An emptied text field is `null`, not `''` — the shape the model stores. */
  private normalised(value: string | null): string | null {
    const trimmed = (value ?? '').trim();
    return trimmed.length ? trimmed : null;
  }

  /**
   * The audience half of the payload -- only what actually moved.
   *
   * The three fields travel together when the audience changes, and only then:
   * `_apply_rekeying` clears what the new audience has no use for, so a
   * customer sent with a global scope is contradicted rather than obeyed.
   * Within one audience, a changed target travels on its own.
   */
  private get audiencePayload(): IUpdatePriceObservationPayload {
    const { audience, customers, groupIds } = this.audienceScope;
    const customerId = customers[0]?.id ?? null;
    const payload: IUpdatePriceObservationPayload = {};
    if (audience && audience !== this.observation.scope) {
      payload.scope = audience as PriceObservationScope;
    }
    if (audience === 'customer' && customerId !== (this.observation.customer_id ?? null)) {
      payload.customer_id = customerId;
    }
    if (audience === 'group' && !sameIds(groupIds, this.observation.group_ids ?? [])) {
      payload.group_ids = groupIds;
    }
    return payload;
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  /**
   * The code an external reference would be keyed on once this correction
   * lands, or '' when the server would write none.
   *
   * Mirrors `_attach_product`'s three conditions, read on the form as it stands
   * rather than on the row as it was loaded: the rows this screen exists for
   * name their customer and attach their product in the same save.
   *
   * An older backend serialises no `learned_code` and ignores
   * `remember_reference`, so nothing is asked. Null means the server says there
   * is no code: a designation teaches an alias, which is not what the dialog
   * offers to decline.
   */
  private get referenceCode(): string {
    if (
      this.pickedProductTemplateId == null
      || this.pickedProductTemplateId === this.observation.product_id
    ) return '';
    const audience = this.audienceScope.audience ?? this.observation.scope;
    if (audience !== 'customer' && audience !== 'unresolved') return '';
    const customer = this.audienceScope.customers[0]?.id ?? this.observation.customer_id;
    if (!customer) return '';
    const code = this.normalised(this.customerCodeRaw) ?? this.observation.learned_code;
    return code ?? '';
  }

  /**
   * SAVE — and, when the correction would teach the matcher, the question
   * about it FIRST.
   *
   * Asked from here rather than from the list, so it stacks on the dialog the
   * operator is still looking at: closing the correction and popping the
   * question afterwards read as an unrelated interruption.
   */
  save(): void {
    if (!this.canSave) return;
    const payload = this.payload;
    const code = this.referenceCode;
    if (!code) {
      this.dialogRef.close({ action: 'save', payload });
      return;
    }
    this.dialog
      .open(RememberReferenceDialogComponent, {
        data: {
          code,
          customerName:
            this.audienceScope.customers[0]?.name || this.observation.customer_name || '',
          productLabel: this.displayOptionFn(this.productControl.value ?? ''),
        } as RememberReferenceDialogData,
        width: '520px',
        maxWidth: '95vw',
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((remember?: boolean) => {
        // The question has no third way out (`disableClose`, no cross, no
        // cancel), so an undefined answer is not a decision to guess at: the
        // correction stays open rather than teaching what nobody asked for.
        if (remember === undefined) return;
        this.dialogRef.close({
          action: 'save',
          payload: { ...payload, remember_reference: remember },
        });
      });
  }

  keepPrice(): void {
    this.dialogRef.close({ action: 'keep-price' });
  }

  remove(): void {
    this.dialogRef.close({ action: 'delete' });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
